import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApplicationsService } from '../../features/apps/services/apps.service';
import { UsersService } from '../../features/users/services/users.service';
import { GroupsService } from '../../features/teams/services/teams.service';
import { User } from '../../features/users/models/user.model';
import { TeamForm } from '../../features/teams/models/team.model';
import { AppForm } from '../../features/apps/models/app.model';
import { InputSearchComponent } from '../input-search/input-search.component';
import { UIInputModule } from '@homepower-co/shared-ui-angular';
import { ModalType } from '../../core/enums/modal-type.enum';

export type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'color'
  | 'file'
  | 'hidden'
  | 'range';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputVariant =
  | 'default'
  | 'primary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';

export type InputValue = string | number | null | undefined;
export interface SearchResult {
  name: string;
  desc: string;
  color: string;
  type?: string;
}

@Component({
  selector: 'app-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    InputSearchComponent,
    UIInputModule,

    // TODO: `HttpClientModule` should not be imported into a component directly.
    // Please refactor the code to add `provideHttpClient()` call to the provider list in the
    // application bootstrap logic and remove the `HttpClientModule` import from this component.
  ],
  templateUrl: './search-modal.component.html',
  styleUrl: './search-modal.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchModalComponent),
      multi: true,
    },
  ],
})
export class SearchModalComponent
  implements ControlValueAccessor, AfterViewInit, OnInit, OnDestroy
{
  @ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput') searchInput!: InputSearchComponent;

  @Input() type = 'text';
  @Input() name = '';
  @Input() placeholder = '';
  private _disabled = false;
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
  }
  get disabled(): boolean {
    return this._disabled;
  }
  @Input() readonly = false;
  @Input() required = false;
  @Input() id = '';
  @Input() tabindex = 0;
  @Input() autofocus = false;
  @Input() autocomplete = '';
  @Input() spellcheck = false;
  @Input() autocapitalize = '';
  @Input() autocorrect = '';
  @Input() maxLength?: number;
  @Input() step: string | number = '';
  @Input() pattern = '';
  @Input() accept = '';
  @Input() multiple = false;

  @Input() ariaLabel = '';
  @Input() ariaDescribedby?: string;
  @Input() role = '';

  @Input() label = '';
  @Input() helperText?: string;
  @Input() errorMessages?: string;
  @Input() variant: InputVariant = 'default';
  @Input() size: InputSize = 'md';

  @Input() startIcon = '';

  @Input() endIcon = 'search';
  @Input() endIconOnClick?: (event: MouseEvent) => void;
  @Output() endIconClick = new EventEmitter<MouseEvent>();

  @Input() help = '';

  @Input() customClasses?: string;
  @Input() customContainerClasses?: string;
  @Input() dataTestId = '';
  @Input() control?: AbstractControl;

  @Output() inputChange = new EventEmitter<Event>();
  @Output() inputInput = new EventEmitter<Event>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputKeyDown = new EventEmitter<KeyboardEvent>();
  @Output() inputKeyUp = new EventEmitter<KeyboardEvent>();
  @Output() inputKeyPress = new EventEmitter<KeyboardEvent>();
  @Output() inputClick = new EventEmitter<MouseEvent>();
  @Output() inputMouseEnter = new EventEmitter<MouseEvent>();
  @Output() inputMouseLeave = new EventEmitter<MouseEvent>();

  @Output() closeEvent = new EventEmitter<void>();

  // Manejo de teclado global para el modal
  selectedIndex = 0;
  results: SearchResult[] = [];
  searchSubscription?: Subscription;
  currentFilter: ModalType | '' = '';
  isLoading = false;
  
  // Hacer el enum disponible en el template
  modalType = ModalType;

  // Subject para manejar el autocompletado con debounce
  private searchSubject = new Subject<string>();

  private static readonly sizeClassMap: Record<InputSize, string> = {
    sm: 'text-xs h-6 px-2',
    md: 'text-sm h-8 px-2',
    lg: 'text-base h-10 px-3',
  };

  private static readonly iconSizeClassMap: Record<InputSize, string> = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl',
  };

  private static readonly helperTextClassName = 'text-xs text-gray-500 mt-1';

  private static readonly errorTextClassName = 'text-xs text-destructive mt-1 space-y-1';

  private usersService = inject(UsersService);
  private teamsService = inject(GroupsService);
  private applicationsService = inject(ApplicationsService);

  constructor() {
    // Configurar el autocompletado con debounce de 300ms
    this.searchSubject
      .pipe(
        debounceTime(300), // Esperar 300ms después de que el usuario deje de escribir
        distinctUntilChanged(), // Solo buscar si el valor cambió
      )
      .subscribe((searchTerm) => {
        if (searchTerm.length >= 3) {
          this.performSearch(searchTerm);
        } else if (searchTerm.length === 0) {
          // Limpiar resultados si se borra todo el texto
          this.results = [];
        }
      });
  }

  /**
   * Realiza la búsqueda real conectándose a los servicios.
   */
  async performSearch(term: string) {
    const termNormalized = this.normalize(term);
    this.results = [];
    this.isLoading = true;

    // Definir qué búsquedas realizar según el filtro
    const searchTasks: Promise<void>[] = [];

    if (!this.currentFilter || this.currentFilter === ModalType.USER) {
      searchTasks.push(this.searchUsers(termNormalized));
    }
    if (!this.currentFilter || this.currentFilter === ModalType.TEAM) {
      searchTasks.push(this.searchTeams(termNormalized));
    }
    if (!this.currentFilter || this.currentFilter === ModalType.APP) {
      searchTasks.push(this.searchApps(termNormalized));
    }

    try {
      await Promise.all(searchTasks);
    } finally {
      this.isLoading = false;
    }

    // Resetear el índice seleccionado al realizar una nueva búsqueda
    this.selectedIndex = 0;
  }

  private async searchUsers(term: string) {
    try {
      // Cargamos una cantidad razonable para búsqueda cliente
      const res = await this.usersService.getUsers(1, 1000).toPromise();
      if (res?.users) {
        const matches = res.users
          .filter(
            (u: User) =>
              this.normalize(u.firstName).includes(term) ||
              this.normalize(u.lastName).includes(term) ||
              this.normalize(u.email).includes(term),
          )
          .map((u: User) => ({
            name: `${u.firstName} ${u.lastName}`,
            desc: u.email,
            color: 'text-orange-400',
            type: 'Usuario',
          }));
        this.results = [...this.results, ...matches];
      }
    } catch (e) {
      console.error('Error searching users', e);
    }
  }

  private async searchTeams(term: string) {
    try {
      const teams = await this.teamsService.getTeams().toPromise();
      if (teams) {
        const matches = teams
          .filter((t: TeamForm) => this.normalize(t.groupName).includes(term))
          .map((t: TeamForm) => ({
            name: t.groupName,
            desc: `Grupo - ${t.application || 'Sin aplicación'}`,
            color: 'text-blue-400',
            type: 'Grupo',
          }));
        this.results = [...this.results, ...matches];
      }
    } catch (e) {
      console.error('Error searching teams', e);
    }
  }

  private async searchApps(term: string) {
    try {
      const apps = await this.applicationsService.getApplications().toPromise();
      if (apps) {
        const matches = apps
          .filter((a: AppForm) => this.normalize(a.appName).includes(term))
          .map((a: AppForm) => ({
            name: a.appName,
            desc: (a.category as string) || 'Aplicación',
            color: 'text-purple-400',
            type: 'Aplicación',
          }));
        this.results = [...this.results, ...matches];
      }
    } catch (e) {
      console.error('Error searching apps', e);
    }
  }
  private normalize(value: string | number | null | undefined): string {
    return (value ?? '').toString().trim().toLowerCase();
  }
  searchTerm = '';

  setFilter(filter: ModalType) {
    this.currentFilter = filter;
    this.results = [];
    // Ejecutar búsqueda automáticamente si hay término
    if (this.searchTerm && this.searchTerm.trim().length >= 3) {
      this.performSearch(this.searchTerm);
    }
  }

  clearFilter() {
    this.currentFilter = '';

    // Si hay un término de búsqueda, realizar búsqueda global
    if (this.searchTerm && this.searchTerm.length >= 3) {
      this.performSearch(this.searchTerm);
    } else {
      // Si no hay término o es muy corto, limpiar resultados
      this.results = [];
    }

    // NO limpiar el searchTerm ni el input visual
    // El usuario puede querer ver resultados globales del mismo término
  }

  clearSearchTerm() {
    this.searchTerm = '';
    this.results = [];

    // Limpiar el input visual
    if (this.searchInput) {
      this.searchInput.clear();
    }

    // Si hay un filtro activo, no lo limpiamos, solo el término de búsqueda
    // El usuario puede querer buscar otro término con el mismo filtro
  }

  getFilterIcon(filter: ModalType): string {
    const icons = {
      [ModalType.USER]: 'person',
      [ModalType.TEAM]: 'people',
      [ModalType.APP]: 'apps'
    };
    return icons[filter];
  }

  getFilterLabel(filter: ModalType): string {
    const labels = {
      [ModalType.USER]: 'Usuario',
      [ModalType.TEAM]: 'Grupo',
      [ModalType.APP]: 'Aplicación'
    };
    return labels[filter];
  }

  // Método que se llama cuando el usuario escribe (autocompletado)
  onSearchInput(term: string) {
    this.searchTerm = term;
    this.searchSubject.next(term);
  }

  // Método que se llama cuando el usuario hace clic en el botón de búsqueda
  onSearch(term: string) {
    this.searchTerm = term;
    if (term.length >= 3) {
      this.performSearch(term);
    } else if (term.length === 0) {
      this.results = [];
    }
  }

  // // Método que realiza la búsqueda real
  class = 'flex items-center gap-1 mb-1';

  @ViewChild('resultsContainer') resultsContainer!: ElementRef<HTMLDivElement>;
  @ViewChildren('resultItem') resultItems!: QueryList<
    ElementRef<HTMLDivElement>
  >;

  ngOnInit() {
    window.addEventListener('keydown', this.handleKeyDown, { capture: true });
  }
  ngAfterViewInit() {
    this.scrollToSelected();
  }
  ngOnDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    // Limpiar el Subject
    this.searchSubject.complete();
  }

  closeModal() {
    window.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    this.closeEvent.emit();
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeModal();
    } else if (event.key === 'Enter') {
      this.onEnter();
    } else if (event.key === 'ArrowDown') {
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.results.length - 1,
      );
      this.scrollToSelected();
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      this.scrollToSelected();
      event.preventDefault();
    }
  };

  scrollToSelected() {
    setTimeout(() => {
      const items = this.resultItems?.toArray();
      if (items && items[this.selectedIndex]) {
        const el = items[this.selectedIndex].nativeElement;
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  onEnter() {
    if (this.results.length > 0 && this.selectedIndex >= 0) {
      this.selectResult(this.results[this.selectedIndex]);
    }
  }

  selectResult(result: SearchResult) {
    void result;
    this.closeModal();
  }

  value: InputValue = '';
  focused = false;
  inputId = `input-${Math.random().toString(36).substring(2, 9)}`;

  private onTouched: () => void = () => void 0;
  private onChangeCallback: (value: InputValue) => void = () => void 0;

  writeValue(value: InputValue): void {
    this.value = value ?? '';
    setTimeout(() => {
      if (this.inputRef?.nativeElement) {
        this.inputRef.nativeElement.value = String(this.value || '');
      }
    });
  }

  registerOnChange(fn: (value: InputValue) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  get hasError(): boolean {
    if (this.control) {
      return (
        this.control.invalid && (this.control.touched || this.control.dirty)
      );
    }
    return Boolean(this.errorMessages && this.errorMessages.trim().length > 0);
  }

  get currentVariant(): InputVariant {
    return this.hasError ? 'error' : this.variant;
  }

  get variantClasses(): Record<InputVariant, string> {
    return {
      default: 'border-secondary',
      primary: 'border-primary',
      error: 'border-destructive',
      success: 'border-success',
      warning: 'border-warning',
      info: 'border-info',
    };
  }

  get sizeClasses(): Record<InputSize, string> {
    return SearchModalComponent.sizeClassMap;
  }

  get iconSizeClasses(): Record<InputSize, string> {
    return SearchModalComponent.iconSizeClassMap;
  }

  get containerClasses(): string {
    const base =
      'flex items-center border-2 rounded-md transition-all duration-150 ease-in-out bg-background-light text-white';
    const focus = this.focused ? 'border-opacity-50' : '';
    const variant = this.variantClasses[this.currentVariant];
    const disabled = this.disabled ? 'opacity-50 cursor-not-allowed' : '';
    const custom = this.customContainerClasses ?? '';
    return [base, variant, focus, disabled, custom].filter(Boolean).join(' ');
  }

  get inputClasses(): string {
    const base =
      'bg-transparent outline-none w-full transition-all duration-150 ease-in-out';
    const size = this.sizeClasses[this.size];
    const custom = this.customClasses ?? '';
    return [base, size, custom].filter(Boolean).join(' ');
  }

  get labelClasses(): string {
    const base = 'block font-medium mb-1';
    const size = this.size === 'lg' ? 'text-sm' : 'text-xs';
    return `${base} ${size}`;
  }

  get iconClasses(): string {
    const size = this.iconSizeClasses[this.size];
    return `text-gray-400 flex-shrink-0 ${size}`;
  }

  get helperTextClasses(): string {
    return SearchModalComponent.helperTextClassName;
  }

  get errorTextClasses(): string {
    return SearchModalComponent.errorTextClassName;
  }

  getAriaDescribedBy(): string {
    const parts: string[] = [];
    if (this.ariaDescribedby) parts.push(this.ariaDescribedby);
    if (this.helperText) parts.push(`${this.inputId}-helper`);
    if (this.hasError) parts.push(`${this.inputId}-error`);
    return parts.join(' ');
  }

  // Event handlers
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let inputValue = target.value;

    // Truncar si excede maxLength
    if (this.maxLength && inputValue.length > this.maxLength) {
      inputValue = inputValue.substring(0, this.maxLength);
      target.value = inputValue;
    }

    this.value = inputValue;
    this.onChangeCallback(this.value);
    this.inputInput.emit(event);
  }

  onKeyDown(event: KeyboardEvent): void {
    this.inputKeyDown.emit(event);
  }

  onChange(event: Event): void {
    this.inputChange.emit(event);
  }

  onFocus(event: FocusEvent): void {
    this.focused = true;
    this.inputFocus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.focused = false;
    this.onTouched();
    this.inputBlur.emit(event);
  }

  onKeyUp(event: KeyboardEvent): void {
    this.inputKeyUp.emit(event);
  }

  onKeyPress(event: KeyboardEvent): void {
    this.inputKeyPress.emit(event);
  }

  onClick(event: MouseEvent): void {
    this.inputClick.emit(event);
  }

  onMouseEnter(event: MouseEvent): void {
    this.inputMouseEnter.emit(event);
  }

  onMouseLeave(event: MouseEvent): void {
    this.inputMouseLeave.emit(event);
  }

  focus(): void {
    this.inputRef?.nativeElement?.focus();
  }

  blur(): void {
    this.inputRef?.nativeElement?.blur();
  }

  select(): void {
    this.inputRef?.nativeElement?.select();
  }

  setSelectionRange(start: number, end: number): void {
    this.inputRef?.nativeElement?.setSelectionRange(start, end);
  }

  getValue(): InputValue {
    return this.value;
  }

  setValue(value: InputValue): void {
    let stringValue = String(value || '');

    // Truncar si excede maxLength
    if (this.maxLength && stringValue.length > this.maxLength) {
      stringValue = stringValue.substring(0, this.maxLength);
    }

    this.value = stringValue;
    this.onChangeCallback(this.value);
    if (this.inputRef?.nativeElement) {
      this.inputRef.nativeElement.value = stringValue;
    }
  }

  clear(): void {
    this.setValue('');
  }

  get nativeElement(): HTMLInputElement | null {
    return this.inputRef?.nativeElement || null;
  }

  get validity(): ValidityState | undefined {
    return this.inputRef?.nativeElement?.validity;
  }

  get validationMessage(): string {
    return this.inputRef?.nativeElement?.validationMessage || '';
  }

  checkValidity(): boolean {
    return this.inputRef?.nativeElement?.checkValidity() || false;
  }

  reportValidity(): boolean {
    return this.inputRef?.nativeElement?.reportValidity() || false;
  }

  setCustomValidity(message: string): void {
    this.inputRef?.nativeElement?.setCustomValidity(message);
  }

  onEndIconClick(event: MouseEvent): void {
    event.stopPropagation();
    this.endIconClick.emit(event);
    if (this.endIconOnClick) {
      this.endIconOnClick(event);
    }
  }
}
